/** @format */

import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { LoyaltyBalance } from '@scorecard/data-access';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { AttributeType } from './attribute-type.enum';
import { ProductPageTemplateType } from './page-template-type.enum';
import {
	AnalyticsService,
	CookieService,
	Logger,
	StoreService,
	UtilityService,
	WINDOW
} from '@shared/data-access/services';

import {
	AnalyticsPricingBuckets,
	ISkuLocation,
	SKUDetails,
	StyleDefiningAttribute
} from '@shared/data-access/interfaces';

/** @dynamic */
@Injectable({
	providedIn: 'root'
})
export class ProductAnalyticsService extends AnalyticsService {
	constructor(
		@Inject(DOCUMENT) protected document: HTMLDocument,
		@Inject(WINDOW) protected window: Window,
		protected cookieService: CookieService,
		protected logger: Logger,
		protected storeService: StoreService,
		protected utilityService: UtilityService
	) {
		super(document, window, cookieService, logger);
	}
	/**
	 * @description
	 * Posts to rumrunner when a missing product image is detected
	 */
	public emitMissingImageEvent(productInfo: {
		color?: string;
		ecode?: string;
		pageUrl: string;
		productName?: string;
		requestedImg: string;
	}): void {
		if (window['elkRUM'] && window['elkRUM'].setCustom) {
			window['elkRUM'].setCustom('missing_image', productInfo);
		}
	}

	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setProductDetails
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @param productDetails - Array of product details
	 *
	 * @description
	 * Sets the SKU details of product
	 */
	public setProductDetails(productDetails: object[]): void {
		this.siteData.ProductDetails = productDetails;
	}

	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setProductArray
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @param productArray - The array of products to set
	 *
	 * @description
	 * Sets an array of products on the Object.
	 */
	public setProductArray(productArray: object): void {
		this.siteData.ProductArray = productArray;
	}

	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setProductId
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @param productId - The product part number
	 *
	 * @description
	 * Sets the Product ID on the Object. ProductID ID is the eCode that is represented
	 * on the PDP. It can be found on the PDP before you resolve to a SKU
	 */
	public setProductId(productId: string): void {
		this.siteData.ProductID = productId;
	}

	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setProductName
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @param productName - The product name
	 *
	 * @description
	 * Sets the Product Name on the Object. Product Name represents the short description associated to the product.
	 */
	public setProductName(productName: string): void {
		this.siteData.ProductName = productName;
	}

	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setProductShippingRestriction
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @param shippingRestrictionFlag - Flag if there is a shipping restriction on the product
	 *
	 * @description
	 * Sets a flag on the Object whether there is a shipping restriction or not.
	 */
	public setProductShippingRestriction(shippingRestrictionFlag: string): void {
		this.siteData.ProductShippingRestriction = shippingRestrictionFlag;
	}

	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setNikeConnectedProduct
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @param shippingRestrictionFlag - Flag if the product is nike exclusive
	 *
	 * @description
	 * Sets a flag on the Object whether there is a nike exclusive product or not.
	 */
	public setNikeConnectedProduct(nikeConnectedProductFlag: boolean): void {
		this.siteData.nikeConnectedProduct = nikeConnectedProductFlag;
	}

	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setTechSpecPresent
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @param techSpecFlag - Whether technical specifications are present or not
	 *
	 * @description
	 * Sets technical specifications flag on the Object as to whether they are present on the product or not.
	 */
	public setTechSpecPresent(techSpecFlag: string): void {
		this.siteData.TechSpecPresent = techSpecFlag;
	}

	/**
	 * @ngdoc method
	 * @name dcsg.app.services.service:AnalyticsService#setProductPageDetailsProperties
	 * @methodOf dcsg.app.services.service:AnalyticsService
	 *
	 * @description
	 * Sets the product details page properties for the resolved product.
	 */

	public setProductPageDetailsProperties(
		product: any,
		skuLevelPricingBuckets: AnalyticsPricingBuckets,
		inventoryMapEcomm: ISkuLocation[],
		inventoryMapBOPIS: ISkuLocation[],
		loyaltyBalance: LoyaltyBalance,
		shippingInfo
	) {
		// Array of product details
		const productDetails = [];

		// Set the page template
		if (!this.siteData.PageTemplate) {
			this.setPageTemplate(product.pageTemplate || 'Standard');
		}

		// Set the product ID
		this.setProductId(product.partNumber || product.style.partNumber);

		// Set the product name
		this.setProductName(product.name || product.style.name);

		if (product.pageTemplate === ProductPageTemplateType.BUNDLE) {
			for (let i = 0, length = product?.ecodes?.length || 0; i < length; i += 1) {
				const bundledProduct = product.ecodes[i];
				productDetails.push(
					this.getProductDetailsQV(
						bundledProduct,
						skuLevelPricingBuckets,
						inventoryMapEcomm,
						inventoryMapBOPIS
					)
				);
			}
		} else {
			productDetails.push(
				this.getProductDetailsQV(
					product,
					skuLevelPricingBuckets,
					inventoryMapEcomm,
					inventoryMapBOPIS
				)
			);
		}
		// Set product details
		this.setProductDetails(productDetails);

		// Set shipping restrictions
		this.setProductShippingRestriction(
			shippingInfo.isHazmat || shippingInfo.isRestricted
				? DCSG_CONSTANTS.analyticsReporting.boolean.true
				: DCSG_CONSTANTS.analyticsReporting.boolean.false
		);

		// Set technical specifications
		this.setTechSpecPresent(
			product.style?.techSpecs
				? DCSG_CONSTANTS.analyticsReporting.boolean.true
				: DCSG_CONSTANTS.analyticsReporting.boolean.false
		);

		if (loyaltyBalance && 'rewardAmount' in loyaltyBalance) {
			// set rewardValue
			this.setRewardValue(loyaltyBalance.rewardAmount);
		}
	}

	private getProductDetailsQV(
		product,
		skuLevelPricingBuckets: AnalyticsPricingBuckets,
		inventoryMapEcomm: ISkuLocation[],
		inventoryMapBOPIS: ISkuLocation[]
	) {
		// Defining attributes of the product
		const productAttributeList = [];

		// SKU details
		const skuDetailsList: SKUDetails[] = [];

		// See Price in Cart String
		const EMPTY_PRICE_STRING = 'See Price in Cart';

		let productGender: string;

		let productType: string;

		let nikeConnectedProduct: boolean;

		//returning true to siteData if product is nike ape exclusive
		product.skus?.filter((obj) => {
			if (obj.descAttributesParsed?.ApeExclusive === 'Nike APE Exclusive') {
				nikeConnectedProduct = true;
			} else {
				nikeConnectedProduct = false;
			}
		});

		this.setNikeConnectedProduct(nikeConnectedProduct);

		// Getting Gender and product Type value and returning it to productDetails
		product.style.descriptiveAttributes.forEach((obj) => {
			if (obj?.name === 'Gender') {
				// eslint-disable-next-line no-unsafe-optional-chaining
				productGender = (obj?.value).toString();
			} else if (obj?.name === 'Product Type') {
				// eslint-disable-next-line no-unsafe-optional-chaining
				productType = (obj?.value).toString();
			}
		});

		// Iterate through defining attributes if available
		if (product.style.definingAttributes) {
			product.style.definingAttributes.forEach((attribute: StyleDefiningAttribute) => {
				// Default the attribute value count to 0
				let attrValueCount = 0;

				// Attempt to split the attribute values
				if (attribute.value.length > 0) {
					attrValueCount = attribute.value.split('|').length;
				}

				// Default attribute type to `AttributeType.BLOCKS`
				let attrType = AttributeType.BLOCKS;

				// Check to see if attribute is `color`
				if (
					typeof attribute.name === 'string' &&
					attribute.name.toLowerCase() === 'color'
				) {
					// Update the attribute type to `AttributeType.SWATCH`
					attrType = AttributeType.SWATCH;
				}

				// Add attribute to Array
				productAttributeList.push({
					Type: attrType,
					Name: attribute.name,
					CountOfOptions: attrValueCount
				});
			});
		}

		// Iterate through SKUs if available
		if (product.skus) {
			// Sets to true by default
			let isAddtoCartAvailable = DCSG_CONSTANTS.analyticsReporting.boolean.true;

			// Sets to false if product is in store only
			if (product.style.isaFlag && !product.style.invFlag && !product.style.bopisFlag) {
				isAddtoCartAvailable = DCSG_CONSTANTS.analyticsReporting.boolean.false;
			}

			product.skus?.forEach((sku: any) => {
				// eComm inventory
				const skuInventoryEcomm = this.storeService.getSkuInventoryFromLocations(
					inventoryMapEcomm,
					sku.partNumber
				);

				// BOPIS inventory
				const skuInventoryBOPIS = this.storeService.getSkuInventoryFromLocations(
					inventoryMapBOPIS,
					sku.partNumber
				);

				// Add the SKU details to the list
				skuDetailsList.push({
					ProductSKU: sku.partNumber,
					SKUCurrentPrice: `${
						sku.prices.offerPrice < sku.prices.mapPrice
							? EMPTY_PRICE_STRING
							: sku.prices.offerPrice || EMPTY_PRICE_STRING
					}`,
					SKUListPrice: `${sku.prices.listPrice || EMPTY_PRICE_STRING}`,
					BOPISAvailable: `${sku.bopisFlag}`,
					AddtoCartAvailable: isAddtoCartAvailable,
					ISAAvailable: `${sku.isaFlag}`,
					ShipClass: `${sku.shipClass}`,
					AvailableForPickup:
						typeof skuInventoryBOPIS !== 'undefined'
							? `${
									this.utilityService.isTruthy(skuInventoryBOPIS.inventory > 0) &&
									sku.bopisFlag
								}`
							: DCSG_CONSTANTS.analyticsReporting.boolean.false,
					AvailableInStore:
						typeof skuInventoryBOPIS !== 'undefined'
							? `${
									this.utilityService.isTruthy(
										skuInventoryBOPIS.isaInventory > 0
									) && sku.isaFlag
								}`
							: DCSG_CONSTANTS.analyticsReporting.boolean.false,
					AvailabletoShip:
						typeof skuInventoryEcomm !== 'undefined'
							? `${this.utilityService.isTruthy(skuInventoryEcomm.inventory > 0)}`
							: DCSG_CONSTANTS.analyticsReporting.boolean.false,
					WebActiveDate: this.formatWebActiveDate(sku.webActiveDate),
					WarrantyEligible: sku.warrantyEligible
				});
			});
		}

		const manufacturer = product.style.descriptiveAttributes.find(
			(descAttr) => descAttr.identifier === 'X_BRAND'
		);

		return {
			ProductAttributeList: productAttributeList,
			ProductBrand: manufacturer?.value || '',
			ProductName: product.style.name,
			ProductID: product.style.partNumber,
			ProductDescriptiveAttributes: product.style.descriptiveAttributes,
			ProductGender: productGender,
			ProductType: productType,
			ProductSKUDetails: skuDetailsList,
			SKUPriceingOptions: skuLevelPricingBuckets
		};
	}

	private formatWebActiveDate(webActiveDate: string): string {
		let formattedDate = 'undefined';
		if (webActiveDate && webActiveDate.includes('T')) {
			// Validate that the part before the "T" matches "YYYY-MM-DD"
			const datePart = webActiveDate.split('T')[0];
			const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

			if (dateRegex.test(datePart)) {
				const dateString = datePart.split('-');
				formattedDate = `${dateString[0]}/${dateString[1]}/${dateString[2]}`;
			}
		}
		return formattedDate;
	}
}
